import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tab,
  Tabs,
  Button,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CheckIcon from '@mui/icons-material/Check';
import UARCACheckSummary from './UARCACheckSummary';
import UARCACheckForm from './UARCACheckForm';
import uarService from '../../../services/UARService';

const UARCACheck = ({ uarFile, appRecord, userswithSOD, rolesForReview, chosenRoles, hrList, onComplete }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [caCheck, setCACheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Load the C&A check data when the component mounts
    const fetchCACheck = async () => {
      try {
        if (uarFile?.id) {
          setLoading(true);
          const data = await uarService.fetchUARCACheck(uarFile.id);
          setCACheck(data);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching C&A check:', error);
        setError('Failed to load C&A check data');
        setLoading(false);
      }
    };

    fetchCACheck();
  }, [uarFile]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleComplete = async () => {
    try {
      setLoading(true);
      
      // Update the UAR file status to "CA Check Completed"
      await uarService.updateUAR(uarFile.id, {
        STATUS: 'CA Check Completed'
      });
      
      setSuccess(true);
      
      // Notify the parent component that the C&A check is complete
      if (onComplete) {
        setTimeout(() => {
          onComplete();
        }, 1500);
      }
    } catch (error) {
      console.error('Error completing C&A check:', error);
      setError('Failed to complete C&A check. Please try again.');
      setLoading(false);
    }
  };

  const handleCACheckCreated = (newCACheck) => {
    setCACheck(newCACheck);
  };

  if (loading && !caCheck) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Completeness & Accuracy check completed successfully! Proceeding to SOD review...
        </Alert>
      )}
      
      <Paper sx={{ mb: 3, p: 2, borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <CheckIcon sx={{ mr: 1, color: 'primary.main' }} />
          Completeness & Accuracy Check
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Verify that all users and roles are correctly included in this review cycle.
          You must complete this check before proceeding to the Segregation of Duties review.
        </Typography>
      </Paper>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="fullWidth"
        >
          <Tab 
            label="Review Data" 
            icon={<AssessmentIcon />} 
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
          <Tab 
            label="Upload Evidence" 
            icon={<FileUploadIcon />} 
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
        </Tabs>
      </Box>
      
      <Box sx={{ p: 1 }}>
        {activeTab === 0 && (
          <UARCACheckSummary 
            uarFile={uarFile}
            appRecord={appRecord}
            userswithSOD={userswithSOD}
            rolesForReview={rolesForReview}
            chosenRoles={chosenRoles}
            hrList={hrList}
          />
        )}
        
        {activeTab === 1 && (
          <UARCACheckForm 
            uarFile={uarFile}
            onComplete={handleComplete}
            onCreated={handleCACheckCreated}
          />
        )}
      </Box>
      
      <Divider sx={{ my: 3 }} />
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        {activeTab === 0 && (
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => setActiveTab(1)}
            endIcon={<FileUploadIcon />}
          >
            Continue to Upload Evidence
          </Button>
        )}
        
        {activeTab === 1 && caCheck?.STATUS === 'Completed' && (
          <Button
            variant="contained"
            color="success"
            onClick={handleComplete}
            startIcon={<CheckIcon />}
            disabled={loading || success}
          >
            {loading ? 'Processing...' : 'Complete C&A Check & Continue'}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default UARCACheck; 